import React, { useState, useEffect } from 'react';
import { sendOrderEmail } from '../../services/email';
import { getLatestNotificationForClient } from '../../services/notifications';
import { getClientInvoices } from '../../services/invoices';
import { useAuth } from '../../context/AuthContext';
import './OrderFormTab.css';

export default function OrderFormTab({ client }) {
  const { user } = useAuth();
  const [orderData, setOrderData] = useState({
    our_po: '',
    order_number: '',
    to: '',
    service_type: 'Measure, Furnish and Install - MFI',
    description: '',
    quantity_dimensions: '',
    specifications: '',
    comments: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loadingNote, setLoadingNote] = useState(true);
  const [invoices, setInvoices] = useState([]);

  // Extract last name from client name
  const getLastName = (fullName) => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(/\s+/);
    return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName;
  };

  // Auto-populate from task completion note
  useEffect(() => {
    const loadCompletionNote = async () => {
      if (!client?.id) return;
      
      try {
        setLoadingNote(true);
        const notification = await getLatestNotificationForClient(client.id);
        
        // Auto-fill "Our P.O." with client's last name
        const lastName = getLastName(client.name);
        
        if (notification?.completion_note) {
          // Try to parse the completion note as JSON (structured data)
          let orderInfo = {};
          try {
            orderInfo = JSON.parse(notification.completion_note);
          } catch (e) {
            // If not JSON, treat as plain text (backward compatibility)
            orderInfo = {
              description: notification.completion_note,
              comments: notification.completion_note,
            };
          }
          
          // Set order data from structured completion note
          setOrderData(prev => ({
            ...prev,
            our_po: lastName,
            to: client.name || '',
            description: orderInfo.description || '',
            quantity_dimensions: orderInfo.quantity_dimensions || '',
            specifications: orderInfo.specifications || '',
            comments: orderInfo.comments || '',
          }));
        } else {
          // If no notification, just set the client name and last name
          setOrderData(prev => ({
            ...prev,
            our_po: lastName,
            to: client.name || '',
          }));
        }
      } catch (error) {
        console.error('Error loading completion note:', error);
        // Still set client name and last name even if note loading fails
        const lastName = getLastName(client.name);
        setOrderData(prev => ({
          ...prev,
          our_po: lastName,
          to: client.name || '',
        }));
      } finally {
        setLoadingNote(false);
      }
    };

    loadCompletionNote();
  }, [client]);

  // Load client invoices for optional "use invoice number as order number" dropdown
  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    getClientInvoices(client.id)
      .then((data) => { if (!cancelled) setInvoices(data || []); })
      .catch(() => { if (!cancelled) setInvoices([]); });
    return () => { cancelled = true; };
  }, [client?.id]);

  // Generate order number (format: YY-NNNN)
  useEffect(() => {
    const generateOrderNumber = () => {
      const year = new Date().getFullYear().toString().slice(-2);
      // For now, use a simple timestamp-based number
      // In production, this should come from a sequence
      const number = String(Date.now()).slice(-4);
      return `${year}-${number}`;
    };

    if (!orderData.order_number) {
      setOrderData(prev => ({
        ...prev,
        order_number: generateOrderNumber(),
      }));
    }
  }, [orderData.order_number]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      await sendOrderEmail(orderData, client, user);
      setSuccess('Order email sent successfully!');
      // Reset form but keep order number and client info
      const currentOrderNumber = orderData.order_number;
      const lastName = getLastName(client.name);
      setOrderData({
        our_po: lastName,
        order_number: currentOrderNumber,
        to: client.name || '',
        service_type: 'Measure, Furnish and Install - MFI',
        description: '',
        quantity_dimensions: '',
        specifications: '',
        comments: '',
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to send order email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingNote) {
    return (
      <div className="order-form-tab">
        <div className="loading-state">Loading order information...</div>
      </div>
    );
  }

  return (
    <div className="order-form-tab">
      <div className="tab-header">
        <p className="tab-subtitle">Send glass orders to orderbay@cvgsonline.com</p>
      </div>

      {success && (
        <div className="success-message">{success}</div>
      )}
      {error && (
        <div className="error-message">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="order-form mfi-form">
        {/* Company Header */}
        <div className="company-header">
          <div className="company-info">
            <h3>MENLO ATHERTON GLASS COMPANY</h3>
            <p>PO BOX 5214</p>
            <p>Redwood City CA 94063</p>
            <p>650-322-1116 Phone</p>
            <p>Heather@menloathertonglass.com</p>
          </div>
        </div>

        {/* Order Information */}
        <div className="order-info-section">
          <div className="form-row">
            <div className="form-group">
              <label>Our P.O.:</label>
              <input
                type="text"
                name="our_po"
                value={orderData.our_po}
                onChange={handleChange}
                placeholder="Car"
              />
            </div>
            <div className="form-group order-number-group">
              <label>Order Number:</label>
              <input
                type="text"
                name="order_number"
                value={orderData.order_number}
                onChange={handleChange}
                className="order-number-input"
                placeholder="e.g., 26-5010"
              />
            </div>
          </div>
          {invoices.length > 0 && (
            <div className="form-row form-row-invoice-link">
              <div className="form-group order-invoice-link-group">
                <label>Use invoice number as order number (optional):</label>
                <select
                  value={invoices.find((inv) => (inv.invoice_number || String(inv.id)) === orderData.order_number)?.id ?? ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    const inv = invoices.find((i) => i.id === id);
                    if (inv) {
                      setOrderData((prev) => ({
                        ...prev,
                        order_number: inv.invoice_number || String(inv.id),
                      }));
                    }
                  }}
                  className="order-invoice-link-select"
                >
                  <option value="">— Select invoice —</option>
                  {invoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number || (inv.type === 'estimate' ? 'Estimate' : 'Invoice')} #{inv.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group sales-associate-info">
              <div className="sales-associate-box">
                <div>{user?.user_metadata?.full_name || user?.email || 'User'}</div>
                <div>Sales Associate</div>
                <div>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div>
                <div className="order-date-label">Order Date</div>
              </div>
            </div>
          </div>
        </div>

        {/* MFI Line Items */}
        <div className="mfi-section">
          <div className="form-group full-width mfi-service-type">
            <input
              type="text"
              name="service_type"
              value={orderData.service_type}
              onChange={handleChange}
              className="mfi-service-type-input"
              placeholder="e.g., Measure, Furnish and Install - MFI"
            />
          </div>
          <div className="mfi-line-items">
            <div className="mfi-line-item">
              <div className="form-group full-width">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  value={orderData.description}
                  onChange={handleChange}
                  placeholder="e.g., Replace IG Unit"
                />
              </div>
              <div className="form-group full-width">
                <label>Quantity / Dimensions / Thickness</label>
                <input
                  type="text"
                  name="quantity_dimensions"
                  value={orderData.quantity_dimensions}
                  onChange={handleChange}
                  placeholder="e.g., 1- 23 7/8&quot; X 26&quot; ... 1/2&quot; OA"
                />
              </div>
              <div className="form-group full-width">
                <label>Specifications</label>
                <input
                  type="text"
                  name="specifications"
                  value={orderData.specifications}
                  onChange={handleChange}
                  placeholder="e.g., Low-E temp / mill / CLR Temp"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="comments-section">
          <div className="form-group full-width">
            <label>Comments</label>
            <textarea
              name="comments"
              value={orderData.comments}
              onChange={handleChange}
              rows="4"
              placeholder="Additional notes or special instructions..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Order Email'}
          </button>
        </div>
      </form>
    </div>
  );
}
